/**
 * PF-3a (fieldiq-api#1219) — the display rules for anomalies, digs, welds and
 * dig reports. Pure functions; the screens import these so a refusal, a status
 * hue and a remaining-WT figure read the same everywhere.
 *
 * THE API DECIDES. `allowedNext` is the API's; this file only words the moves
 * it offers. Remaining WT % is the API's figure and is never recomputed here —
 * an absent figure prints `—` with the API's reason, never 0.
 */
import { fmtNumber, pfApiError, pfDay, pfRefusal, type PfSkin, PF_UNKNOWN_SKIN } from './pipeline-facility';

// ─── Status skins ────────────────────────────────────────────────────────────

export const PF_DIG_STATUS_SKIN: Record<string, PfSkin> = {
  planned: { label: 'Planned', fill: 'bg-fi-signal-slate', rail: 'border-l-fi-steel' },
  excavated: { label: 'Excavated', fill: 'bg-fi-signal-amber', rail: 'border-l-fi-amber' },
  assessed: { label: 'Assessed', fill: 'bg-fi-signal-blue', rail: 'border-l-fi-blue' },
  repaired: { label: 'Repaired', fill: 'bg-fi-signal-blue', rail: 'border-l-fi-blue' },
  recoated: { label: 'Recoated', fill: 'bg-fi-signal-blue', rail: 'border-l-fi-blue' },
  backfilled: { label: 'Backfilled', fill: 'bg-fi-signal-green', rail: 'border-l-fi-green' },
  reported: { label: 'Reported', fill: 'bg-fi-signal-green', rail: 'border-l-fi-green' },
};
export const pfDigStatusSkin = (status: string | null | undefined): PfSkin =>
  (status && PF_DIG_STATUS_SKIN[status]) || PF_UNKNOWN_SKIN;

export const PF_REPORT_STATUS_SKIN: Record<string, PfSkin> = {
  draft: { label: 'Draft', fill: 'bg-fi-signal-amber', rail: 'border-l-fi-amber' },
  issued: { label: 'Issued', fill: 'bg-fi-signal-green', rail: 'border-l-fi-green' },
};
export const pfReportStatusSkin = (status: string | null | undefined): PfSkin =>
  (status && PF_REPORT_STATUS_SKIN[status]) || PF_UNKNOWN_SKIN;

export const PF_WELD_RESULT_SKIN: Record<string, PfSkin> = {
  pending: { label: 'Pending', fill: 'bg-fi-signal-slate', rail: 'border-l-fi-steel' },
  accept: { label: 'Accept', fill: 'bg-fi-signal-green', rail: 'border-l-fi-green' },
  repair: { label: 'Repair', fill: 'bg-fi-signal-amber', rail: 'border-l-fi-amber' },
  cut_out: { label: 'Cut out', fill: 'bg-fi-signal-red', rail: 'border-l-fi-red' },
};
export const pfWeldResultSkin = (r: string | null | undefined): PfSkin => (r && PF_WELD_RESULT_SKIN[r]) || PF_UNKNOWN_SKIN;

export const PF_ASSESSMENT_SKIN: Record<string, PfSkin> = {
  acceptable: { label: 'Acceptable', fill: 'bg-fi-signal-green', rail: 'border-l-fi-green' },
  monitor: { label: 'Monitor', fill: 'bg-fi-signal-blue', rail: 'border-l-fi-blue' },
  repair: { label: 'Repair', fill: 'bg-fi-signal-amber', rail: 'border-l-fi-amber' },
  cut_out: { label: 'Cut out', fill: 'bg-fi-signal-red', rail: 'border-l-fi-red' },
};
/** No assessment yet reads as "Not assessed", a state the dig cannot pass `assessed` with. */
export const pfAssessmentSkin = (r: string | null | undefined): PfSkin =>
  (r && PF_ASSESSMENT_SKIN[r]) || { label: 'Not assessed', fill: 'bg-fi-signal-slate', rail: 'border-l-fi-steel' };

// ─── Status moves ────────────────────────────────────────────────────────────

/** The button for a move, by where it goes. */
export const PF_DIG_MOVE_LABEL: Record<string, string> = {
  excavated: 'Mark excavated',
  assessed: 'Mark assessed',
  repaired: 'Mark repaired',
  recoated: 'Mark recoated',
  backfilled: 'Mark backfilled',
};

/**
 * The status steps a person may PATCH, from the API's `allowedNext`. `reported`
 * is dropped: a dig is reported only by issuing its report (the API refuses a
 * PATCH to it with `report_required`), so it is never drawn as a status button.
 */
export function pfDigSteps(allowedNext: readonly string[] | null | undefined): string[] {
  return (Array.isArray(allowedNext) ? allowedNext : []).filter((s) => s !== 'reported');
}

/** A dig waiting on its report: backfilled, and the machine's only way on is `reported`. */
export const pfDigAwaitsReport = (status: string | null | undefined) => status === 'backfilled';

/** Writes stop once a dig is reported (and once its job is closed). */
export const pfDigEditable = (status: string | null | undefined, jobStatus: string | null | undefined) =>
  status !== 'reported' && jobStatus !== 'completed' && jobStatus !== 'cancelled';

// ─── Remaining wall thickness ────────────────────────────────────────────────

/** The API's remaining-WT % as written, or `—` with the API's reason. Never 0 for absent. */
export function remainingWtText(pct: number | null | undefined, reason: string | null | undefined): string {
  if (pct !== null && pct !== undefined && Number.isFinite(pct)) return `${fmtNumber(pct)}%`;
  if (reason === 'no_nominal_wt') return '— (no nominal WT on the site or segment)';
  if (reason === 'no_measurement') return '— (no UT minimum or pit depth)';
  return '—';
}

export const remainingWtBasisLabel = (basis: string | null | undefined): string =>
  basis === 'ut_min_wt' ? 'from UT minimum' : basis === 'pit_depth' ? 'from pit depth' : '';

// ─── Refusals ────────────────────────────────────────────────────────────────

const str = (v: unknown): string | null => (typeof v === 'string' && v ? v : null);
const statusWord = (s: string) => pfDigStatusSkin(s).label.toLowerCase();

/**
 * Every refusal code the PF-3a routes send, as one plain sentence. A code this
 * map lacks goes to `pfRefusal` (PF-2's sentences), then the API's `error`.
 */
const PF_DIG_REFUSALS: Record<string, (x: Record<string, unknown>) => string> = {
  illegal_transition: (x) => {
    const next = pfDigSteps(Array.isArray(x.allowedNext) ? (x.allowedNext as string[]) : []);
    return next.length
      ? `The dig cannot move to that status. From here it can go to: ${next.map(statusWord).join(' or ')}.`
      : 'The dig cannot move to that status from where it is.';
  },
  report_required: () => 'A dig is marked reported by issuing its dig report, not by changing its status.',
  features_unassessed: (x) => {
    const n = typeof x.unassessed === 'number' ? x.unassessed : null;
    return n === 1
      ? '1 feature on this dig has no assessment result yet. Assess every feature before marking the dig assessed.'
      : `${n ?? 'Some'} features on this dig have no assessment result yet. Assess every feature before marking the dig assessed.`;
  },
  no_repair_recorded: () => 'Record the repair (a method other than none) before marking the dig repaired.',
  repair_required: () =>
    'A feature here was assessed for repair or cut-out and no repair is recorded. Record the sleeve, wrap or cut-out before recoating.',
  future_day: (x) => `That date is after today${str(x.today) ? ` (${pfDay(str(x.today))})` : ''}. Enter the day it happened.`,
  job_closed: () => 'This job is completed or cancelled, so its digs and welds can no longer be changed.',
  dig_reported: () => 'This dig is reported. Its sheet, features and repairs are final.',
  backfill_before_excavation: () => 'The backfill date cannot be before the excavation date.',
  dig_not_planned: () => 'Only a planned dig can be deleted. This one has been worked on.',
  anomaly_site_mismatch: () => 'That anomaly is on a different site from this job.',
  already_on_dig: () => 'That anomaly is already a feature on this dig.',
  pit_depth_exceeds_wt: () => 'The pit depth is deeper than the nominal wall thickness. Check the measurement.',
  feature_has_repair: () => 'A repair is recorded against this feature. Remove the repair first.',
  feature_not_on_dig: () => 'That feature belongs to another dig.',
  repair_in_use: () => 'Welds or pressure tests are linked to this repair. Remove those links first.',
  segment_site_mismatch: () => 'That segment belongs to a different site.',
  not_on_crew: () => 'You are not on this job\'s crew, so you cannot work its digs.',
  duplicate_anomaly: () => 'This anomaly is already in the list for this site.',
  feature_id_conflict: () => 'Another anomaly on this site already uses that feature ID.',
  anomaly_in_use: (x) => {
    const digs = Array.isArray(x.digs) ? (x.digs as unknown[]).map((d) => (typeof d === 'string' ? d : str((d as { digNumber?: unknown })?.digNumber))).filter(Boolean) : [];
    return digs.length
      ? `This anomaly is planned on ${digs.join(', ')}. Remove it from the dig before deleting it.`
      : 'This anomaly is planned on a dig. Remove it from the dig before deleting it.';
  },
  weld_number_taken: () => 'That weld number is already used. Weld numbers are unique across the company.',
  repair_not_on_job: () => 'That repair belongs to a dig on another job.',
  invalid_upload: () => 'The uploaded report could not be attached. Upload it again.',
  already_linked: () => 'That weld is already linked.',
  report_exists: () => 'This dig already has a report. Open it instead.',
  report_issued: () => 'This report is issued. An issued report cannot be changed.',
  already_issued: () => 'This report is already issued.',
  job_cancelled: () => 'The job is cancelled, so its dig report cannot be issued.',
  dig_not_backfilled: () => 'The dig report is issued once the dig is backfilled. Backfill the dig first.',
  signatory_unknown: () => 'Your user account has no name to sign the report with. Add your name to your profile.',
  report_changed: () => 'The report changed while it was being issued. Try again.',
  // PF-3b (fieldiq-api#1224) — the AI parse of an anomaly list and the dig report summary draft.
  // ai_unavailable answers both doors with a door-specific sentence, so the API's own words are used.
  ai_unavailable: (x) => str(x.error) ?? 'The AI is not available right now. Nothing was saved. Try again later.',
  ai_parse_failed: (x) =>
    `${str(x.error) ?? 'The AI could not read this list. Nothing was saved.'} Try again, or import the list as CSV / XLSX instead.`,
  ai_draft_failed: () => 'The AI could not draft a summary this time. Nothing was changed. Try again, or write the summary yourself.',
  run_dismissed: () => 'That AI read was discarded, so its rows can no longer be saved. Read the list again to import it.',
  run_not_pending: (x) =>
    `This AI draft is already ${str(x.outcome) ?? 'closed'}, so it can no longer be approved or discarded. Reload the page.`,
};

export const PF_DIG_REFUSAL_CODES = Object.keys(PF_DIG_REFUSALS);

export function pfDigRefusal(err: unknown, fallback: string): string {
  const e = pfApiError(err);
  if (e.code && PF_DIG_REFUSALS[e.code]) return PF_DIG_REFUSALS[e.code](e.extra);
  return pfRefusal(err, fallback);
}

/** The id a 409 points at (`existingId`) — the duplicate anomaly, the existing report, the linked weld. */
export function refusalExistingId(err: unknown): string | null {
  return str(pfApiError(err).extra.existingId);
}

// ─── Small parsing helpers for forms ─────────────────────────────────────────

/** '' → null; a number string → number; anything else → NaN (the caller says so). */
export function numOrNull(s: string): number | null {
  const t = s.trim();
  if (t === '') return null;
  return Number(t);
}

/** The coating line: type and condition, `—` when neither is recorded. */
export function coatingText(type: string | null | undefined, condition: string | null | undefined): string {
  const parts = [type, condition].filter((x): x is string => !!x && x.trim() !== '');
  return parts.length ? parts.join(' · ') : '—';
}
